import { useState } from "react";
import { FiPhone, FiMessageSquare, FiCheckCircle } from "react-icons/fi";

const BACKEND_URL = "https://portfolio-backend-2jmg.onrender.com/sms/send";
const ADMIN_PHONE = "+19782353397";
const CALENDAR_LINK =
  "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ03sd-0CHEcrQu5qh1vewPZM18ZWXRbHylT1LhZXEE-hqjE8f_FcoGH73Ui9FWNqL4slHE6NVxN";

const C = {
  bg: "#ffffff",
  heroBg: "linear-gradient(180deg, #faf9ff 0%, #ffffff 100%)",
  surface: "#f7f7f8",
  border: "#e5e5e7",
  text: "#111111",
  muted: "#6b7280",
  accent: "#6c2fe6",
  accentHover: "#5a22cc",
  accentLight: "#ede9fe",
};

function formatPhone(raw) {
  const digits = raw.replace(/\D/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function toE164(raw) {
  const digits = raw.replace(/\D/g, "");
  return digits.startsWith("1") ? `+${digits}` : `+1${digits}`;
}

export default function App() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("idle");
  const [btnHover, setBtnHover] = useState(false);

  async function handleSubmit() {
    if (!name.trim() || phone.replace(/\D/g, "").length < 10) return;
    setStatus("loading");
    const e164 = toE164(phone);
    try {
      const [notifyRes, replyRes] = await Promise.all([
        fetch(BACKEND_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: ADMIN_PHONE,
            body: `New lead from multiply-growth.com!\nName: ${name}\nPhone: ${e164}`,
          }),
        }),
        fetch(BACKEND_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: e164,
            body: `Hey ${name}! This is Ed from Multiply. Thanks for reaching out. Book a call: multiply-growth.com/call`,
          }),
        }),
      ]);
      if (notifyRes.ok && replyRes.ok) {
        setStatus("success");
        setName("");
        setPhone("");
        setTimeout(() => setStatus("idle"), 5000);
        window.gtag("event", "form_submit", {
          event_category: "lead",
          event_label: "sms_request",
        });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }

  const inputStyle = {
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: "8px",
    padding: "13px 16px",
    fontSize: "0.95rem",
    color: C.text,
    outline: "none",
    fontFamily: "Inter, sans-serif",
    width: "100%",
    boxSizing: "border-box",
  };

  const steps = [
    {
      num: "01",
      icon: <FiPhone size={16} color={C.accent} />,
      title: "Customer calls, you miss it",
      body: "Happens to every business. The window to respond is minutes, not hours.",
    },
    {
      num: "02",
      icon: <FiMessageSquare size={16} color={C.accent} />,
      title: "They get an instant text",
      body: "Our system fires an SMS within seconds, keeping the conversation alive.",
    },
    {
      num: "03",
      icon: <FiCheckCircle size={16} color={C.accent} />,
      title: "You get a booked job",
      body: "The lead stays warm. You close more business without lifting a finger.",
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: C.bg,
        color: C.text,
        fontFamily: "Inter, sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; }
        .btn-primary {
          width: 100%; padding: 14px; border-radius: 8px;
          background: ${C.accent}; color: #fff; border: none;
          font-size: 0.9rem; font-weight: 600; cursor: pointer;
          font-family: Inter, sans-serif;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: background 0.15s, transform 0.1s;
        }
        .btn-primary:hover:not(:disabled) { background: ${C.accentHover}; transform: scale(1.01); }
        .btn-primary:active:not(:disabled) { transform: scale(0.99); }
        .btn-primary:disabled { background: ${C.accentLight}; color: ${C.accent}; cursor: not-allowed; }
        .btn-secondary {
          display: inline-block; padding: 13px 28px;
          background: ${C.bg}; border: 1px solid ${C.border};
          border-radius: 8px; color: ${C.text}; text-decoration: none;
          font-size: 0.875rem; font-weight: 500; font-family: Inter, sans-serif;
          transition: border-color 0.15s, background 0.15s;
        }
        .btn-secondary:hover { border-color: ${C.accent}; color: ${C.accent}; }
        input:focus { border-color: ${C.accent} !important; box-shadow: 0 0 0 3px ${C.accentLight}; }
        .nav-link { font-size: 0.85rem; color: ${C.muted}; text-decoration: none; font-weight: 500; transition: color 0.15s; }
        .nav-link:hover { color: ${C.accent}; }
      `}</style>

      {/* Nav */}
      <nav
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "1.25rem 2rem",
          borderBottom: `1px solid ${C.border}`,
          background: C.bg,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <img
            src="/multiply.png"
            alt="Multiply"
            style={{ height: "26px", width: "auto" }}
          />
          <span
            style={{
              fontWeight: 600,
              fontSize: "1rem",
              color: C.text,
              letterSpacing: "-0.01em",
            }}
          >
            Multiply
          </span>
        </div>
        <a
          href={CALENDAR_LINK}
          target="_blank"
          rel="noreferrer"
          className="nav-link"
        >
          Book a Call
        </a>
      </nav>

      {/* Hero */}
      <section style={{ background: C.heroBg, padding: "6rem 2rem 4rem" }}>
        <div
          style={{ maxWidth: "620px", margin: "0 auto", textAlign: "center" }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "7px",
              background: C.accentLight,
              borderRadius: "999px",
              padding: "5px 14px",
              marginBottom: "2rem",
            }}
          >
            <FiMessageSquare size={12} color={C.accent} />
            <span
              style={{
                fontSize: "0.72rem",
                color: C.accent,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              Automated SMS Response
            </span>
          </div>

          <h1
            style={{
              fontWeight: 600,
              fontSize: "clamp(2.4rem, 6vw, 3.8rem)",
              lineHeight: 1.1,
              letterSpacing: "-0.03em",
              margin: "0 0 1.25rem",
              color: C.text,
            }}
          >
            Grow faster with <br />
            <span style={{ color: C.accent }}>custom SMS</span>
            <br />
            solutions!
          </h1>

          <p
            style={{
              fontSize: "1.05rem",
              color: C.muted,
              lineHeight: 1.7,
              margin: "0 auto 3rem",
              maxWidth: "460px",
            }}
          >
            Miss a call? We text them back instantly — before they call your
            competitor.
          </p>

          {status === "success" ? (
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: "12px",
                padding: "2.5rem",
              }}
            >
              <FiCheckCircle
                size={30}
                color="#16a34a"
                style={{ marginBottom: "0.75rem" }}
              />
              <p
                style={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: C.text,
                  margin: "0 0 0.4rem",
                }}
              >
                Check your phone.
              </p>
              <p style={{ fontSize: "0.875rem", color: C.muted, margin: 0 }}>
                You just experienced exactly what we build for our clients.
              </p>
            </div>
          ) : (
            <div
              style={{
                background: "#fff",
                border: `1px solid ${C.border}`,
                borderRadius: "12px",
                padding: "1.75rem",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              }}
            >
              <p
                style={{
                  fontSize: "0.72rem",
                  color: C.muted,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  margin: "0 0 1.25rem",
                  fontWeight: 600,
                }}
              >
                See it in action — text yourself right now
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  marginBottom: "10px",
                }}
              >
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={inputStyle}
                />
                <input
                  type="tel"
                  placeholder="Your phone number"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  maxLength={14}
                  style={inputStyle}
                />
              </div>
              <button
                className="btn-primary"
                onClick={handleSubmit}
                disabled={
                  status === "loading" ||
                  !name.trim() ||
                  phone.replace(/\D/g, "").length < 10
                }
              >
                <FiMessageSquare size={15} />
                {status === "loading" ? "Sending..." : "Text Me Back"}
              </button>
              {status === "error" && (
                <p
                  style={{
                    color: "#dc2626",
                    fontSize: "0.82rem",
                    marginTop: "0.75rem",
                    textAlign: "center",
                  }}
                >
                  Something went wrong.{" "}
                  <a
                    href={CALENDAR_LINK}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: C.accent }}
                  >
                    Book a call directly.
                  </a>
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* How it works */}
      <section
        style={{
          maxWidth: "620px",
          margin: "0 auto",
          padding: "4rem 2rem 5rem",
        }}
      >
        <p
          style={{
            fontSize: "0.72rem",
            color: C.muted,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            marginBottom: "2rem",
            fontWeight: 600,
          }}
        >
          How it works
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {steps.map((step) => (
            <div
              key={step.num}
              style={{
                display: "flex",
                gap: "1.25rem",
                alignItems: "flex-start",
                padding: "1.25rem 1.5rem",
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: "10px",
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  fontSize: "1.4rem",
                  color: C.accent,
                  letterSpacing: "-0.04em",
                  lineHeight: 1,
                  flexShrink: 0,
                  minWidth: "32px",
                  userSelect: "none",
                }}
              >
                {step.num}
              </span>
              <div>
                <p
                  style={{
                    margin: "0 0 3px",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    color: C.text,
                  }}
                >
                  {step.title}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.84rem",
                    color: C.muted,
                    lineHeight: 1.6,
                  }}
                >
                  {step.body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: "2.5rem" }}>
          <a
            href={CALENDAR_LINK}
            target="_blank"
            rel="noreferrer"
            className="btn-secondary"
          >
            Book a Free Call
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{
          borderTop: `1px solid ${C.border}`,
          padding: "2rem",
          textAlign: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            marginBottom: "6px",
          }}
        >
          <img
            src="/multiply.png"
            alt="Multiply"
            style={{ height: "18px", width: "auto", opacity: 0.5 }}
          />
          <span style={{ fontSize: "0.8rem", color: C.muted, fontWeight: 500 }}>
            Multiply
          </span>
        </div>
        <p
          style={{
            margin: "0 0 4px",
            fontSize: "0.78rem",
            color: "#9ca3af",
            fontStyle: "italic",
          }}
        >
          Never miss a lead.
        </p>
        <p style={{ margin: 0, fontSize: "0.72rem", color: "#d1d5db" }}>
          © 2025 Multiply · multiply-growth.com
        </p>
      </footer>
    </div>
  );
}
